import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ACLService } from '@delon/acl';
import { _HttpClient, SettingsService } from '@delon/theme';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ReportingService } from 'src/app/core/util/reporting.service';

import { ReportTypeItem } from '../preview/preview-doc.component';

type BatchFormatMode = 'all' | 'eInvOnly' | 'nonEInvOnly';

@Component({
  selector: 'app-batch-print',
  templateUrl: './batch-print.component.html',
  styleUrls: ['./batch-print.component.less'],
  standalone: false
})
export class BatchPrintComponent implements OnChanges {
  @Input() reportTypes: ReportTypeItem[] = [];
  @Input() selectedRows: any[] = [];
  @Input() country?: string;

  reportFormats: any[] = [];
  visibleFormats: any[] = [];
  refreshing = false;
  opening = false;

  constructor(
    private acl: ACLService,
    private http: _HttpClient,
    private reporting: ReportingService,
    private settings: SettingsService,
    private msg: NzMessageService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reportTypes']) {
      this.loadReportFormats();
      return;
    }

    if (changes['selectedRows'] || changes['country']) {
      this.updateVisibleFormats();
    }
  }

  get hasSelection(): boolean {
    return this.getSelectedDocCodes().length > 0;
  }

  get canBatchPrint(): boolean {
    return this.hasSelection && this.visibleFormats.length > 0 && !this.refreshing;
  }

  async openFormat(reportFormat: any) {
    if (!reportFormat || !this.canBatchPrint) return;

    if (reportFormat.isEInvFormat && this.hasDraftOrExemptedSelection()) {
      this.msg.error(
        'A Draft/Exempted transaction is selected.  Cannot use Batch Print with an E-Invoice format.'
      );
      return;
    }

    const selection = this.getSelectedDocCodes();
    if (selection.length === 0) return;

    const filter = { selection };
    this.opening = true;

    try {
      const url = await this.reporting.generateLink(
        reportFormat.reportType,
        reportFormat.reportUrl,
        filter,
        true
      );

      if (url) {
        window.open(url as string, '_blank');
      } else {
        this.msg.error('Unable to open batch print preview.');
      }
    } catch {
      this.msg.error('Unable to open batch print preview.');
    } finally {
      this.opening = false;
    }
  }

  reloadFormats() {
    this.loadReportFormats();
  }

  private loadReportFormats() {
    if (!this.reportTypes?.length) {
      this.reportFormats = [];
      this.visibleFormats = [];
      return;
    }

    const allowedReportTypes = this.reportTypes.filter(x =>
      x.acl ? this.acl.can(x.acl) : this.acl.can(`${x.reportType}.view`)
    );

    if (!allowedReportTypes.length) {
      this.reportFormats = [];
      this.visibleFormats = [];
      return;
    }

    const formatTypes = allowedReportTypes.map(x => x.reportType).join(',');
    this.refreshing = true;

    this.http
      .get(`${this.reporting.reportServerMirrorUrl}/api/ReportFiles/GetByType`, {
        reportType: formatTypes
      })
      .subscribe({
        next: (res: any[]) => {
          const formats = res ?? [];
          const orderingControl = this.getOrderingControl();

          if (orderingControl !== null && this.isAllowedReportType(formatTypes)) {
            const transactionType = this.getReportType(formatTypes);
            if (transactionType) {
              this.http.get('api/TenantData/EInvDefault').subscribe({
                next: (eInvData: any[]) => {
                  const defaultReport = eInvData
                    ? this.reorderReportFormats(
                        eInvData,
                        orderingControl,
                        transactionType
                      )
                    : null;

                  if (defaultReport) {
                    this.reportFormats = [
                      ...formats.filter(r => r.reportUrl === defaultReport),
                      ...formats.filter(r => r.reportUrl !== defaultReport)
                    ];
                  } else {
                    this.reportFormats = [...formats];
                  }
                  this.refreshing = false;
                  this.updateVisibleFormats();
                },
                error: () => {
                  this.reportFormats = [...formats];
                  this.refreshing = false;
                  this.updateVisibleFormats();
                }
              });
              return;
            }
          }

          this.reportFormats = [...formats];
          this.refreshing = false;
          this.updateVisibleFormats();
        },
        error: () => {
          this.reportFormats = [];
          this.visibleFormats = [];
          this.refreshing = false;
        }
      });
  }

  private updateVisibleFormats() {
    const mode = this.getBatchMode();
    let formats = [...this.reportFormats];

    if (mode === 'eInvOnly') {
      formats = formats.filter(r => r.isEInvFormat);
    } else if (mode === 'nonEInvOnly') {
      formats = formats.filter(r => !r.isEInvFormat);
    }

    this.visibleFormats = formats;
  }

  private getBatchMode(): BatchFormatMode {
    const country = this.country || this.settings.user?.['company']?.country;
    if (country !== 'MY') return 'all';

    if (this.hasDraftOrExemptedSelection()) return 'nonEInvOnly';

    return 'all';
  }

  private getOrderingControl(): number | null {
    const controls = this.getSelectedControls();
    if (controls.length === 0) return null;

    const uniqueControls = new Set(controls);
    if (uniqueControls.size !== 1) return null;

    return [...uniqueControls][0] ?? null;
  }

  private getSelectedControls(): number[] {
    return this.selectedRows
      .map(row =>
        this.normalizeControl(
          row?.eInvoiceControl ??
            row?.einvoiceControl ??
            row?.einvControl ??
            row?.EInvoiceControl
        )
      )
      .filter((value): value is number => value !== null);
  }

  private hasDraftOrExemptedSelection(): boolean {
    const controls = this.getSelectedControls();
    return controls.some(value => value === 0 || value === 3);
  }

  private normalizeControl(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private getSelectedDocCodes(): string[] {
    const docCodes = this.selectedRows
      .map(
        row =>
          row?.docCode ??
          row?.DocCode ??
          row?.docNo ??
          row?.docNumber ??
          row?.code
      )
      .filter(Boolean);

    return Array.from(new Set(docCodes));
  }

  private isAllowedReportType(reportType: string): boolean {
    const allowedReportTypes = [
      'sinv-voucher',
      'sdn-voucher',
      'scn-voucher',
      'cs-voucher',
      'pinv-voucher',
      'pdn-voucher',
      'pcn-voucher',
      'cp-voucher'
    ];

    return allowedReportTypes.includes(reportType);
  }

  private getReportType(reportTypes: string) {
    switch (reportTypes) {
      case 'sinv-voucher':
        return 'SINV';
      case 'sdn-voucher':
        return 'SDN';
      case 'scn-voucher':
        return 'SCN';
      case 'cs-voucher':
        return 'CS';
      case 'pinv-voucher':
        return 'PINV';
      case 'pdn-voucher':
        return 'PDN';
      case 'pcn-voucher':
        return 'PCN';
      case 'cp-voucher':
        return 'CP';
      default:
        return '';
    }
  }

  private reorderReportFormats(
    eInvData: any[],
    einvControl: number,
    transactionType: string
  ) {
    const einvControlMap: { [key: number]: string } = {
      0: 'draft',
      1: 'portal',
      2: 'full',
      3: 'exempted'
    };

    const formatKey = einvControlMap[einvControl];

    if (transactionType && formatKey) {
      const transaction = eInvData.find(
        t => t.transactionType === transactionType
      );
      return transaction ? transaction[formatKey] : null;
    }
    return null;
  }
}
